/* Schedule — read-only, by design.
 *
 * The question this answers is "when can we take this in", so it shows the
 * planned work windows and where they collide, not a plan the app made.
 * **Do not add auto-scheduling** (IDEA.md §7): every version of it in trades
 * software gets switched off within a week, because the foreman knows things
 * the model doesn't.
 *
 * A row is the job it belongs to, then one CSS grid over the days, which
 * draws four things on the same track set:
 *   · the in-shop band  — dashed, goods in to promised
 *   · the deadline mark — a red rule on the day it must be out by
 *   · the work bar      — est. start for est. days
 *   · a side label      — the bar's text when the bar is too narrow to hold it
 *
 * The grid is framed by the viewport rather than by its own content: it is
 * the only thing that scrolls, and both its edges are frozen against that
 * scroll — the date header to the top, the job column to the left. Twelve
 * weeks across and twenty jobs down, a bar you cannot name and a day you
 * cannot read is a grid nobody trusts. */
import { useState, type CSSProperties } from 'react';
import { RANGES } from '@/data/reference';
import {
  addDays,
  compareDays,
  daysBetween,
  formatDay,
  formatDayShort,
  formatWeekday,
  isWeekend,
  mondayOf,
  today as todayInShop,
  type IsoDay,
} from '@/domain/time';
import { areaFor } from '@/domain/items';
import { atRisk, deadlineAt, isOverdue, planWindow, scheduled } from '@/domain/schedule';
import { useApp } from '@/store/useApp';
import type { RailSort } from '@/store/useApp';
import { PageHead, Seg } from '@/ui/controls';
import { Swatch } from '@/ui/Swatch';
import type { Job, Plan, Stage } from '@/types';
import { usePageGutter, withGutter } from '@/ui/viewport';

/* How the unscheduled panel can be ordered.
 *
 * Three questions the office asks of a job nobody has promised a date yet:
 * what came in first, what is owed back soonest, and what would fit in a gap.
 * Nearest first means the same thing in all three — what needs an answer
 * soonest sits at the top — and each row draws the date it was ordered on, so
 * the order is readable rather than asserted. A job missing that date falls to
 * the bottom rather than sorting as day 0: no date is not an early one. */
const RAIL_SORTS: {
  key: RailSort;
  label: string;
  /** What the order is on, or null when the job has no such thing. A day or a
   *  count, and each key always gives the same one — days compare as strings
   *  because that is what an ISO day is for, counts compare as numbers, and
   *  `<` does the right thing for both. */
  of: (j: Job) => IsoDay | number | null;
  /** What that value reads as on the card. */
  note: (j: Job) => string;
}[] = [
  {
    key: 'in',
    label: 'Date in',
    of: (j) => j.received,
    note: (j) => (j.received === null ? 'Not in yet' : `In ${formatDay(j.received)}`),
  },
  {
    key: 'deadline',
    label: 'Deadline',
    of: (j) => deadlineAt(j.plan),
    note: (j) =>
      j.plan.deadline === null ? 'No deadline' : `Out by ${formatDay(j.plan.deadline)}`,
  },
  {
    key: 'days',
    label: 'Est. days',
    of: (j) => j.plan.estDays || null,
    note: (j) => (j.plan.estDays ? `${j.plan.estDays} days est.` : 'No estimate'),
  },
];

/** The frozen first column — the job the row is about, held against the
 *  horizontal scroll so a bar in week nine still says whose it is. */
const JOB_COL = 248;

export function ScheduleScreen() {
  const gutter = usePageGutter();
  const jobs = useApp((s) => s.jobs);
  const stages = useApp((s) => s.stages);
  const range = useApp((s) => s.range);
  const from = useApp((s) => s.rangeFrom);
  const railOpen = useApp((s) => s.railOpen);
  const railSort = useApp((s) => s.railSort);
  const set = useApp((s) => s.set);
  const openJob = useApp((s) => s.openJob);

  /* What the grid is, asked for rather than always on. The sentence answered a
     question nobody asks twice, and it cost a line above a grid that is now
     sized to the screen it is read on. It is session state, not the shop's. */
  const [aboutOpen, setAboutOpen] = useState(false);

  /** A stage's name off the admin's list — the schedule labels jobs with it in
   *  two places, and both have to follow a rename. */
  const stageNameOf = (id: string) => stages.find((s) => s.id === id)?.name ?? id;

  const dayCount = range;
  // The window runs `from` (always a Monday) to `last`, inclusive. Every offset
  // the grid draws is relative to `from`, not to day 0.
  const last = addDays(from, dayCount - 1);
  const today = todayInShop();
  const thisWeek = mondayOf(today);
  // Past a month, a column is a week — a 91-day grid of single days is unreadable.
  const isWeeks = dayCount > 28;
  const unit = isWeeks ? 7 : 1;
  const nCols = Math.ceil(dayCount / unit);

  /** The planned work window, falling back to the in-shop span when nothing is
   *  planned — a job with no estimate still occupies the racks. */
  const workWin = (j: Job): { s: IsoDay; e: IsoDay } => {
    const planned = planWindow(j.plan);
    if (planned) return planned;
    // Nothing planned: the job still occupies the racks from the day it came in
    // to the day it is promised. A job with neither sits on its promised day
    // alone, which is the only day anybody knows about it.
    const s = j.received ?? j.promised ?? today;
    const e = j.promised ?? s;
    return { s: s < e ? s : e, e: s < e ? e : s };
  };

  const onBoard = jobs.filter(
    (j) => scheduled(j) && !stages.find((s) => s.id === j.stage)?.terminal,
  );

  const cols = Array.from({ length: nCols }, (_, c) => {
    const day = addDays(from, c * unit);
    const to = addDays(day, unit - 1);
    const live = onBoard.filter((j) => {
      const w = workWin(j);
      return w.s <= to && w.e >= day;
    });
    return {
      day,
      isToday: today >= day && today <= to,
      weekend: isWeekend(day) && !isWeeks,
      dow: isWeeks ? `Wk ${c + 1}` : formatWeekday(day),
      num: isWeeks ? `from ${formatDayShort(day)}` : formatDayShort(day),
      load: live.length
        ? `${live.length} on · ${live.reduce((a, j) => a + areaFor(j), 0).toFixed(0)} m²`
        : '',
    };
  });

  // A row earns a lane only if its planned window — or, for late work still on
  // the bench, its in-shop span — actually touches the visible range. Work that
  // ran out before the window is pinned in only where *today* is: page forward
  // and the past stops following you. That used to be day 0, the Monday the
  // board was pinned to; today is what it always meant.
  const visible = onBoard.filter((j) => {
    const w = workWin(j);
    if (w.s > last) return false;
    if (w.e < from) {
      const inShop = (j.received ?? w.s) <= last && (j.promised ?? w.e) >= from;
      return inShop || (isOverdue(j, stages) && from <= today && last >= today);
    }
    return true;
  });

  const shown = new Set(visible.map((j) => j.code));
  const offRange = onBoard.filter((j) => !shown.has(j.code));
  const laterCount = offRange.filter((j) => workWin(j).s > last).length;
  const earlierCount = offRange.filter((j) => workWin(j).e < from).length;

  // What the window is hiding, and the two ways to see it.
  const were = (n: number) => (n === 1 ? 'job is' : 'jobs are');
  const offRangeNote = [
    earlierCount ? `${earlierCount} ${were(earlierCount)} planned before this window` : '',
    laterCount
      ? `${laterCount} ${were(laterCount)} planned after ${earlierCount ? 'it' : 'this window'}`
      : '',
  ]
    .filter(Boolean)
    .join(', ');

  const bars = visible
    .slice()
    .sort(
      (a, b) =>
        compareDays(workWin(a).s, workWin(b).s) || compareDays(a.promised ?? '', b.promised ?? ''),
    )
    .map((j) => layout(j, j.plan, workWin(j), from, last, unit, nCols, stages));

  const sort = RAIL_SORTS.find((o) => o.key === railSort) ?? RAIL_SORTS[0];
  const unscheduled = jobs
    .filter((j) => !scheduled(j))
    .sort((a, b) => {
      const x = sort.of(a);
      const y = sort.of(b);
      // Nothing to order on goes last whichever column is picked: a job with
      // no date is the one that needs a decision, not one that happened early.
      if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
      return x < y ? -1 : x > y ? 1 : 0;
    });
  const rangeLabel = `${formatDay(from)} – ${formatDay(last)}`;
  // A week at a time, both ways, and no floor: the board runs on real days now
  // rather than on offsets from a pinned Monday, so there is no earliest week
  // to stop at. Today is the anchor, and the Today button is how you get back.
  const step = (weeks: number) => set('rangeFrom', addDays(from, weeks * 7));

  /* The days are their own grid beside the job column rather than the first
     track of one grid across the row: a grid item is held inside its own grid
     area, so a sticky job cell in track one could never leave it, and the
     frozen column would scroll away with everything else. */
  const dayCols = `repeat(${nCols},minmax(0,1fr))`;

  return (
    <section
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        /* The page gutter and the foot. The band above takes its sides from
           this and adds only its own two vertical paddings — the gutter is the
           chrome's own, so the title lines up with the brand and the
           destinations over it, and the card under it. */
        padding: withGutter(gutter, 0, 20),
      }}
    >
      {/* The one row every destination opens with. This screen's controls are
          the widest of the five — a range, a week stepper and This week — so it
          is the one with no caption: what the grid is sits behind the ⓘ instead,
          because a sentence read once should not take a line off a grid that is
          now sized to the screen. */}
      <PageHead
        title="Schedule"
        beside={
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            aria-expanded={aboutOpen}
            aria-controls="schedule-about"
            aria-label="About the schedule"
            title="About the schedule"
            onClick={() => setAboutOpen(!aboutOpen)}
            style={{
              marginLeft: -6,
              borderRadius: 999,
              fontFamily: 'var(--font-data)',
              fontSize: 14,
              ...(aboutOpen
                ? {
                    background: 'var(--accent-tint)',
                    borderColor: 'var(--accent)',
                    color: 'var(--accent-deep)',
                  }
                : null),
            }}
          >
            i
          </button>
        }
        below={
          aboutOpen ? (
            <p
              id="schedule-about"
              style={{
                margin: '10px 0 0',
                padding: '10px 14px',
                borderRadius: 12,
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                fontSize: 14,
                lineHeight: 1.45,
                color: 'var(--ink-muted)',
              }}
            >
              Planned work windows. Overlapping bars are jobs running at once. Read-only — the board
              is where work moves.
            </p>
          ) : null
        }
      >
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <Seg
            options={RANGES.map((r) => ({
              label: r.label,
              on: range === r.days,
              pick: () => set('range', r.days),
            }))}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              title="Back a week"
              aria-label="Back a week"
              onClick={() => step(-1)}
            >
              ‹
            </button>
            <span
              style={{
                fontFamily: 'var(--font-data)',
                fontSize: 13,
                color: 'var(--ink-muted)',
                whiteSpace: 'nowrap',
                minWidth: 168,
                textAlign: 'center',
              }}
            >
              {rangeLabel}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              title="Forward a week"
              aria-label="Forward a week"
              onClick={() => step(1)}
            >
              ›
            </button>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ minHeight: 36, fontSize: 12 }}
            disabled={from === thisWeek}
            onClick={() => set('rangeFrom', thisWeek)}
          >
            This week
          </button>
        </div>
      </PageHead>

      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          margin: '0 0 14px',
          flexWrap: 'wrap',
        }}
      >
        <Key
          swatch={{
            background: 'var(--bar-bg)',
            border: '1px solid var(--bar-line)',
            borderRadius: 999,
          }}
        >
          Planned work — est. start for est. days
        </Key>
        <Key swatch={{ border: '1px dashed var(--line)', borderRadius: 6 }}>
          In the shop — goods in to promised
        </Key>
        <Key swatch={{ width: 3, background: 'var(--urgent)', borderRadius: 2, height: 15 }}>
          Deadline
        </Key>
        <Key
          swatch={{
            background: 'var(--urgent-tint)',
            border: '1px solid var(--urgent)',
            borderRadius: 999,
          }}
        >
          Past deadline or overdue
        </Key>

        {/* The unscheduled jobs open on a button rather than sitting beside the
            grid: the panel costs a quarter of the screen, and the grid is what
            the shop came here to read. The count stays on the button whichever
            way it is set, so nothing is hidden by closing it. */}
        <button
          type="button"
          className="btn btn-secondary"
          aria-expanded={railOpen}
          aria-controls="unscheduled-panel"
          onClick={() => set('railOpen', !railOpen)}
          title={railOpen ? 'Hide unscheduled jobs' : 'Show unscheduled jobs'}
          style={{
            marginLeft: 'auto',
            minHeight: 36,
            fontSize: 12,
            ...(railOpen
              ? {
                  background: 'var(--accent-tint)',
                  borderColor: 'var(--accent)',
                  color: 'var(--accent-deep)',
                }
              : null),
          }}
        >
          Unscheduled
          <span
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 11.5,
              padding: '1px 7px',
              borderRadius: 999,
              background: railOpen ? 'var(--accent)' : 'var(--line-soft)',
              color: railOpen ? 'var(--on-accent)' : 'var(--ink-muted)',
            }}
          >
            {unscheduled.length}
          </span>
        </button>
      </div>

      {/* The grid is framed by the viewport, not by its own content: the row
          that scrolls is this one, and `minmax(0,1fr)` is what stops an `auto`
          row growing past the bottom of the screen. The floor is the other
          half — on a screen too short to hold it, the page scrolls again
          rather than squeezing the grid into a slot. */}
      <div
        style={{
          flex: 1,
          minHeight: 320,
          display: 'grid',
          gridTemplateColumns: railOpen ? 'minmax(0,1fr) 268px' : 'minmax(0,1fr)',
          gridTemplateRows: 'minmax(0,1fr)',
          gap: 18,
          alignItems: 'stretch',
        }}
      >
        {/* This is the scrollport both frozen edges are measured against: the
            date header sticks to its top, the job column to its left. */}
        <div
          className="blueprint"
          style={{ background: 'var(--surface)', padding: 0, minHeight: 0, overflow: 'auto' }}
        >
          <div style={{ minWidth: Math.max(920, JOB_COL + nCols * (isWeeks ? 152 : 74)) }}>
            <div
              style={{
                display: 'flex',
                position: 'sticky',
                top: 0,
                zIndex: 6,
                background: 'var(--ground)',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <div
                className="label-xs"
                style={{
                  width: JOB_COL,
                  flex: 'none',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  padding: '10px 12px',
                  letterSpacing: '0.12em',
                  background: 'var(--ground)',
                  borderRight: '1px solid var(--line)',
                }}
              >
                Job
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'grid',
                  gridTemplateColumns: dayCols,
                }}
              >
                {cols.map((c) => (
                  <div
                    key={c.day}
                    style={{
                      padding: '10px 8px',
                      borderLeft: '1px solid var(--line-soft)',
                      background: c.weekend ? 'var(--surface-2)' : 'transparent',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 5,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span className="label-xs" style={{ letterSpacing: '0.1em' }}>
                        {c.dow}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-data)',
                          fontSize: 11.5,
                          fontWeight: c.isToday ? 600 : 400,
                          color: c.isToday ? 'var(--inv-ink)' : 'var(--ink)',
                          background: c.isToday ? 'var(--inv-bg)' : undefined,
                          padding: c.isToday ? '1px 6px' : undefined,
                          borderRadius: c.isToday ? 999 : undefined,
                        }}
                      >
                        {c.num}
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-data)',
                        fontSize: 10.5,
                        color: 'var(--ink-muted)',
                        minHeight: 14,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.load}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {bars.map((b) => (
              <div
                key={b.job.code}
                style={{
                  display: 'flex',
                  borderTop: '1px solid var(--line-soft)',
                  minHeight: 48,
                  alignItems: 'stretch',
                }}
              >
                <button
                  type="button"
                  onClick={() => openJob(b.job.code)}
                  style={{
                    width: JOB_COL,
                    flex: 'none',
                    position: 'sticky',
                    left: 0,
                    zIndex: 5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '8px 12px',
                    background: 'var(--surface)',
                    border: 0,
                    borderRight: '1px solid var(--line)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    minWidth: 0,
                    minHeight: 44,
                  }}
                >
                  <Swatch code={b.job.colour} size={22} />
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        style={{ fontFamily: 'var(--font-data)', fontSize: 11.5, fontWeight: 500 }}
                      >
                        {b.job.code}
                      </span>
                      <span className="label-xs" style={{ fontSize: 9, letterSpacing: '0.09em' }}>
                        {stageNameOf(b.job.stage)}
                      </span>
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {b.job.customer}
                    </span>
                  </span>
                  {b.overdue ? (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontFamily: 'var(--font-heading)',
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: '0.09em',
                        textTransform: 'uppercase',
                        color: 'var(--on-urgent)',
                        background: 'var(--urgent)',
                        padding: '2px 7px',
                        borderRadius: 999,
                      }}
                    >
                      Overdue
                    </span>
                  ) : null}
                </button>

                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'grid',
                    gridTemplateColumns: dayCols,
                    alignItems: 'center',
                  }}
                >
                  {cols.map((c, i) => (
                    <div
                      key={c.day}
                      style={{
                        gridRow: 1,
                        gridColumn: i + 1,
                        alignSelf: 'stretch',
                        borderLeft: '1px solid var(--line-soft)',
                        background: c.weekend ? 'var(--surface-2)' : 'transparent',
                      }}
                    />
                  ))}

                  <div
                    title="In the shop"
                    style={{
                      gridRow: 1,
                      gridColumn: `${b.bandS + 1} / span ${Math.max(1, b.bandE - b.bandS + 1)}`,
                      zIndex: 1,
                      alignSelf: 'center',
                      height: 44,
                      margin: '0 1px',
                      borderRadius: 14,
                      border: '1px dashed var(--line)',
                      background: 'transparent',
                    }}
                  />

                  {b.dlCol !== null ? (
                    <div
                      title={`Deadline ${formatDay(b.plan?.deadline)}`}
                      style={{
                        gridRow: 1,
                        gridColumn: b.dlCol + 1,
                        zIndex: 3,
                        justifySelf: 'end',
                        width: 3,
                        height: 40,
                        marginRight: 2,
                        borderRadius: 2,
                        background: 'var(--urgent)',
                      }}
                    />
                  ) : null}

                  <div
                    title={b.barTitle}
                    style={{
                      gridRow: 1,
                      gridColumn: `${b.s + 1} / span ${b.span}`,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: '0 4px',
                      minHeight: 30,
                      padding: '0 12px',
                      fontFamily: 'var(--font-data)',
                      fontSize: 11,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      borderRadius: 999,
                      background: b.hot ? 'var(--urgent-tint)' : 'var(--bar-bg)',
                      border: `1px solid ${b.hot ? 'var(--urgent)' : 'var(--bar-line)'}`,
                      color: b.hot ? 'var(--urgent)' : 'var(--ink)',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {b.wide ? b.text : ''}
                    </span>
                  </div>

                  {!b.wide && b.run ? (
                    <div
                      style={{
                        gridRow: 1,
                        gridColumn: `${b.run.col} / span ${b.run.span}`,
                        zIndex: 4,
                        padding: '0 9px',
                        textAlign: b.run.right ? 'right' : 'left',
                        fontFamily: 'var(--font-data)',
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        pointerEvents: 'none',
                        color: b.hot ? 'var(--urgent)' : 'var(--ink-muted)',
                      }}
                    >
                      {b.text}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            {offRangeNote ? (
              <p
                style={{
                  margin: 0,
                  padding: '12px 16px',
                  borderTop: '1px solid var(--line-soft)',
                  fontSize: 12.5,
                  color: 'var(--ink-muted)',
                }}
              >
                <span style={{ position: 'sticky', left: 0, display: 'inline-block' }}>
                  {offRangeNote}. Step the window a week at a time, or widen the range.
                </span>
              </p>
            ) : null}

            {bars.length === 0 ? (
              <p
                style={{
                  margin: 0,
                  padding: '20px 16px',
                  fontSize: 13.5,
                  color: 'var(--ink-muted)',
                }}
              >
                <span style={{ position: 'sticky', left: 0, display: 'inline-block' }}>
                  Nothing in the shop in this window. Step to another week, widen the range, or open
                  a job to set its promised date.
                </span>
              </p>
            ) : null}
          </div>
        </div>

        {railOpen ? (
          <div
            className="blueprint"
            id="unscheduled-panel"
            role="region"
            aria-label="Unscheduled jobs"
            style={{
              background: 'var(--surface)',
              padding: 16,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 2,
              }}
            >
              <h2
                style={{
                  fontSize: 14,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: 0,
                }}
              >
                Unscheduled
              </h2>
              <span
                style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--ink-muted)' }}
              >
                {unscheduled.length}
              </span>
              <button
                type="button"
                title="Hide unscheduled jobs"
                aria-label="Hide unscheduled jobs"
                onClick={() => set('railOpen', false)}
                style={{
                  marginLeft: 'auto',
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: '1px solid var(--line)',
                  background: 'var(--surface)',
                  color: 'var(--ink-muted)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                ›
              </button>
            </div>
            <p
              style={{
                flex: 'none',
                margin: '0 0 10px',
                fontSize: 12.5,
                color: 'var(--ink-muted)',
                lineHeight: 1.4,
              }}
            >
              No promised date yet. Open a job to set one.
            </p>

            {/* Ordered by whichever date the office is working to. The three
                chips share the rail's width on one line — an equal flex basis
                and type a step down, so none of them wraps or is cut short. */}
            <div style={{ flex: 'none', marginBottom: 12 }}>
              <div className="label-xs" style={{ marginBottom: 6, letterSpacing: '0.1em' }}>
                Order by
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {RAIL_SORTS.map((o) => {
                  const on = o.key === sort.key;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => set('railSort', o.key)}
                      title={`Nearest first by ${o.label.toLowerCase()}`}
                      style={{
                        flex: '1 1 0',
                        minWidth: 0,
                        minHeight: 36,
                        padding: '0 6px',
                        borderRadius: 999,
                        cursor: 'pointer',
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 600,
                        fontSize: 10,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        background: on ? 'var(--accent)' : 'var(--surface)',
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--line)'}`,
                        color: on ? 'var(--on-accent)' : 'var(--ink-muted)',
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* The rail is as tall as the grid beside it, so the cards scroll
                in here rather than running the page past the bottom. */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {unscheduled.map((j) => (
                <button
                  key={j.code}
                  type="button"
                  onClick={() => openJob(j.code)}
                  style={{
                    textAlign: 'left',
                    background: 'var(--ground)',
                    border: '1px solid var(--line)',
                    borderRadius: 12,
                    padding: 10,
                    cursor: 'pointer',
                    minHeight: 48,
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 3,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Swatch code={j.colour} size={14} />
                    <span
                      style={{ fontFamily: 'var(--font-data)', fontSize: 11.5, fontWeight: 500 }}
                    >
                      {j.code}
                    </span>
                  </span>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>
                    {j.customer}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontFamily: 'var(--font-data)',
                      fontSize: 10.5,
                      color: 'var(--ink-muted)',
                    }}
                  >
                    {j.items} items · {stageNameOf(j.stage)}
                  </span>
                  {/* The value the list is in order of, so the order reads as
                      a fact about the job rather than as an arrangement. */}
                  <span
                    style={{
                      display: 'block',
                      marginTop: 2,
                      fontFamily: 'var(--font-data)',
                      fontSize: 10.5,
                      color: 'var(--ink)',
                    }}
                  >
                    {sort.note(j)}
                  </span>
                </button>
              ))}
              {unscheduled.length === 0 ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: 12.5,
                    color: 'var(--ink-muted)',
                    border: '1px dashed var(--line)',
                    borderRadius: 12,
                    padding: 12,
                    lineHeight: 1.45,
                  }}
                >
                  Every job in the shop has a promised date. Nothing waiting to be booked in.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Where one job's bar, band, deadline mark and side label sit in the grid. */
function layout(
  job: Job,
  plan: Plan | undefined,
  win: { s: IsoDay; e: IsoDay },
  from: IsoDay,
  last: IsoDay,
  unit: number,
  nCols: number,
  stages: Stage[],
) {
  const dl = deadlineAt(plan);
  const overdue = isOverdue(job, stages);
  const risk = atRisk(plan);
  const hot = risk || overdue;

  /** A day as the grid column that holds it, clamped to the window.
   *
   *  Every column number on this row — the bars, the in-shop band, the deadline
   *  mark and the side label — is an index into the day grid, worked out from
   *  real days rather than from offsets to a Monday pinned in 2026. Shifting
   *  one is shifting all five. */
  const col = (day: IsoDay) =>
    Math.floor(daysBetween(from, day < from ? from : day > last ? last : day) / unit);

  const bandS = col(job.received ?? win.s);
  const bandE = col(job.promised ?? job.received ?? win.e);
  const s = col(win.s);
  const e = col(win.e);
  const span = Math.max(1, e - s + 1);

  // Arrows say the window runs off the edge of the visible range.
  const early = win.s < from;
  const late = win.e > last;
  const dlCol = dl === null || dl < from || dl > last ? null : col(dl);

  const text = `${early ? '◂ ' : ''}${plan?.estDays || '?'} days · ${job.items} items${
    risk ? ' · past deadline' : overdue ? ' · overdue' : ''
  }${late ? ' ▸' : ''}`;

  const wide = span >= 2;

  // A narrow bar puts its text beside itself. The label goes in the widest free
  // run of columns — never under the bar, never across the deadline mark.
  const runs: { col: number; span: number; right: boolean }[] = [];
  const roomRight = nCols - (s + span);
  const dlAhead = dlCol !== null && dlCol >= s + span;
  if (dlAhead) {
    runs.push({ col: dlCol + 2, span: nCols - (dlCol + 1), right: false });
    runs.push({ col: s + 1 + span, span: dlCol - (s + span), right: false });
  } else if (roomRight > 0) {
    runs.push({ col: s + 1 + span, span: roomRight, right: false });
  }
  if (s > 0) runs.push({ col: 1, span: dlCol !== null && dlCol < s ? dlCol : s, right: true });
  const run = runs.filter((r) => r.span >= 2).sort((a, b) => b.span - a.span)[0] ?? null;

  return {
    job,
    plan,
    overdue,
    hot,
    bandS,
    bandE,
    s,
    span,
    dlCol,
    text,
    wide,
    run,
    barTitle: `${job.code} — planned ${formatDay(plan?.estStart)} for ${
      plan?.estDays ?? '?'
    } days · promised ${formatDay(job.promised)} · deadline ${formatDay(plan?.deadline)}`,
  };
}

function Key({ swatch, children }: { swatch: CSSProperties; children: string }) {
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        fontSize: 12,
        color: 'var(--ink-muted)',
      }}
    >
      <span style={{ width: 26, height: 14, ...swatch }} />
      {children}
    </span>
  );
}
